import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';
import { LocalItem } from '~Types';
import { useBasketCalculator } from '~Scenes/CreateOrder/Components/Basket';
import { useI18n, useSelector, useCurrency } from '~Hooks';
import TouchedWrapper from '~Scenes/SelfServiceKiosks/Components/TouchedWrapper';
import { useItemsPackager } from '~Utils/packagedItems';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  bottom: 113px;
  left: 0;
  width: 100%;
  pointer-events: none;

  @media (orientation: landscape) {
    bottom: 63px;
  }
`;

const pulsate = keyframes`
  50% { transform: scale(0.9); }
  100% { transform: scale(1); }
`;

const Inner = styled(TouchedWrapper)<{ animation: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 216px;
  width: 216px;
  background: rgb(19, 19, 22);
  border-radius: 80px;
  box-shadow: 0px 11px 15px -7px rgba(0, 0, 0, 0.32), 0px 9px 46px 8px rgba(0, 0, 0, 0.2),
    0px 24px 38px 3px rgba(0, 0, 0, 0.24);
  pointer-events: all;
  animation: ${({ animation }) =>
    animation
      ? css`
          ${pulsate} .6s linear
        `
      : ''};

  @media (orientation: landscape) {
    height: 162px;
    width: 162px;
    border-radius: 60px;
  }
`;

const Price = styled.div`
  color: rgb(255, 255, 255);
  font-family: Eina01-Regular;
  font-size: 24px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 32px;
  text-align: center;

  @media (orientation: landscape) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const Label = styled.div`
  color: rgb(255, 255, 255);
  font-family: Eina01-SemiBold;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0px;
  line-height: 32px;
  text-align: center;
  margin: 0 0 24px 0;

  @media (orientation: landscape) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const Quantity = styled.div`
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: rgb(66, 66, 69);
  background: rgb(255, 255, 255);
  font-family: Eina01-SemiBold;
  font-size: 14px;
  font-weight: 600;

  & span {
    position: relative;
    top: -1px;
  }
`;

interface Props {
  orderId: string | null;
  items: LocalItem[];
  venueId: string;
  onClick: () => void;
  quantity: number;
}

const SmallCart = ({ onClick, quantity, venueId, items, orderId }: Props) => {
  const { i18n } = useI18n();
  const calculator = useBasketCalculator(venueId);
  const currency = useCurrency();
  const order = useSelector((state) => state.POSOrders.find((o) => o.id === orderId));
  const [animation, setAnimation] = useState(false);

  useEffect(() => {
    setAnimation(true);
    setTimeout(() => setAnimation(false), 600);
  }, [quantity]);

  const packaged = useItemsPackager(
    items,
    venueId,
    order?.discountRate ?? null,
    order?.discountType ?? null,
    true,
  );

  const [total] = calculator(packaged);

  return (
    <Wrapper>
      <Inner onClick={onClick} animation={animation}>
        <Price>{currency.format(total)}</Price>
        <Label>{i18n('Express.Cart.Title')}</Label>
        <Quantity>
          <span>{quantity}</span>
        </Quantity>
      </Inner>
    </Wrapper>
  );
};

export default SmallCart;
